import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const READING_IMAGES = [
  'https://readdy.ai/api/search-image?query=A%20Korean%20manhwa%20webtoon%20style%20comic%20panel%20scene%20with%20two%20characters%20having%20an%20emotional%20conversation%20in%20a%20beautiful%20garden%2C%20soft%20watercolor%20shading%2C%20clean%20lineart%2C%20vertical%20scroll%20format%2C%20warm%20color%20palette&width=800&height=1200&seq=reading-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20dramatic%20Korean%20manhwa%20webtoon%20style%20comic%20panel%20showing%20a%20close-up%20of%20a%20character%20face%20with%20intense%20expression%2C%20detailed%20eyes%2C%20wind-blown%20hair%2C%20dramatic%20lighting%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20manhwa%20webtoon%20style%20comic%20panel%20depicting%20a%20magical%20transformation%20scene%20with%20glowing%20effects%2C%20sparkles%2C%20flowing%20fabric%2C%20ethereal%20atmosphere%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20romantic%20Korean%20manhwa%20webtoon%20style%20comic%20panel%20of%20a%20couple%20holding%20hands%20under%20cherry%20blossoms%2C%20soft%20pink%20tones%2C%20tender%20moment%2C%20delicate%20petals%20falling%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20tense%20Korean%20manhwa%20webtoon%20style%20comic%20panel%20of%20a%20confrontation%20scene%20with%20dynamic%20action%20lines%2C%20two%20characters%20facing%20off%2C%20dark%20atmosphere%2C%20rain%20effects%2C%20vertical%20scroll%20format&width=800&height=1200&seq=reading-page-05&orientation=portrait',
];

export default function ComicReadPage() {
  const { id, chapterNum } = useParams<{ id: string; chapterNum: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);
    const found = comics.find((c) => c.id === Number(id));
    setComic(found || null);
    setLoading(false);
  }, [id]);

  const chapter = useMemo(() => {
    if (!comic || !chapterNum) return null;
    return comic.chapters.find((ch) => ch.number === Number(chapterNum)) || null;
  }, [comic, chapterNum]);

  const currentIndex = useMemo(() => {
    if (!comic || !chapter) return -1;
    return comic.chapters.findIndex((ch) => ch.number === chapter.number);
  }, [comic, chapter]);

  const prevChapter = currentIndex > 0 ? comic?.chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < (comic?.chapters.length || 0) - 1 ? comic?.chapters[currentIndex + 1] : null;

  const [showChapterList, setShowChapterList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="mt-3 text-foreground-500 text-sm">加载中...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!comic || !chapter) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-600 text-lg">章节未找到</p>
            <Link
              to={comic ? `/comic/${comic.id}` : '/'}
              className="mt-4 inline-block px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              {comic ? '返回漫画详情' : '返回首页'}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} ${chapter.title} - 虚空韩漫网`,
        url: `https://szlzjsm.cn/comic/${comic.id}/read/${chapter.number}`,
        author: { '@type': 'Person', name: comic.author },
        datePublished: chapter.updateDate,
        dateModified: chapter.updateDate,
        description: `${comic.title} ${chapter.title}，虚空韩漫网提供免费正版韩漫下拉式阅读。`,
        publisher: { '@type': 'Organization', name: '虚空韩漫网' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://szlzjsm.cn' },
          {
            '@type': 'ListItem',
            position: 2,
            name: comic.title,
            item: `https://szlzjsm.cn/comic/${comic.id}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: chapter.title,
            item: `https://szlzjsm.cn/comic/${comic.id}/read/${chapter.number}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-950 min-h-screen">
        {/* Reading Top Bar */}
        <div className="sticky top-16 z-30 bg-background-900/95 backdrop-blur-sm border-b border-background-700/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-12 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to={`/comic/${comic.id}`}
                className="text-background-300 hover:text-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
              </Link>
              <span className="text-sm text-background-200 truncate max-w-[200px] md:max-w-md">
                {comic.title} - {chapter.title}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setShowChapterList(!showChapterList); setShowSettings(false); }}
                className="text-background-300 hover:text-background-100 transition-colors text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-list-unordered"></i> 目录
              </button>
              <button
                onClick={() => { setShowSettings(!showSettings); setShowChapterList(false); }}
                className="text-background-300 hover:text-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-settings-3-line"></i>
              </button>
            </div>
          </div>

          {/* Chapter List Dropdown */}
          {showChapterList && (
            <div className="absolute left-0 right-0 top-full bg-background-900 border-b border-background-700/30 max-h-60 overflow-y-auto">
              <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                  {comic.chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}/read/${ch.number}`}
                      className={`px-3 py-2 rounded text-sm transition-colors whitespace-nowrap ${
                        ch.number === chapter.number
                          ? 'bg-primary-500/20 text-primary-400'
                          : 'text-background-300 hover:bg-background-800 hover:text-background-100'
                      }`}
                    >
                      {ch.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Settings Dropdown */}
          {showSettings && (
            <div className="absolute right-4 md:right-6 top-full mt-1 bg-background-800 rounded-lg p-4 shadow-lg border border-background-700/30 z-40 min-w-[200px]">
              <p className="text-xs text-background-400 mb-2">阅读设置</p>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded text-xs bg-background-700 text-background-200 whitespace-nowrap cursor-pointer">浅色</button>
                <button className="px-3 py-1.5 rounded text-xs bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer">深色</button>
              </div>
            </div>
          )}
        </div>

        {/* Reading Content - Scroll/下拉式 */}
        <div className="max-w-[800px] mx-auto py-6 px-4">
          <div className="flex flex-col gap-0">
            {READING_IMAGES.map((img, idx) => (
              <div key={idx} className="w-full">
                <img
                  src={img}
                  alt={`${comic.title} ${chapter.title} - 第${idx + 1}页`}
                  title={`${comic.title} ${chapter.title} - 虚空韩漫网`}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>

          {/* Chapter Navigation */}
          <div className="flex items-center justify-between mt-8 mb-4">
            {prevChapter ? (
              <Link
                to={`/comic/${comic.id}/read/${prevChapter.number}`}
                className="flex items-center gap-1 px-5 py-2.5 rounded-full bg-background-800 text-background-200 text-sm hover:bg-background-700 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <div></div>
            )}

            <Link
              to={`/comic/${comic.id}`}
              className="px-5 py-2.5 rounded-full bg-background-800 text-background-200 text-sm hover:bg-background-700 transition-all whitespace-nowrap cursor-pointer"
            >
              返回详情
            </Link>

            {nextChapter ? (
              <Link
                to={`/comic/${comic.id}/read/${nextChapter.number}`}
                className="flex items-center gap-1 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="px-5 py-2.5 rounded-full bg-background-800 text-background-400 text-sm whitespace-nowrap">
                已是最后一话
              </span>
            )}
          </div>
        </div>
      </main>

      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </>
  );
}